import React from 'react';

import {Button, Container, Paper, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation, useQueryClient} from 'react-query';

import API, {AccountUpdateRequest, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {ConfirmationDialog, Head, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  errorNoUser: {
    id: 'pages.member.EmailUpdatePage.error.noUesr',
    defaultMessage: 'user not found',
  },
  errorValueNotFound: {
    id: 'pages.member.EmailUpdatePage.error.valueNotFound',
    defaultMessage: 'value not found',
  },
  errorInvalidEmail: {
    id: 'pages.member.EmailUpdatePage.error.invalidEmail',
    defaultMessage: 'invalid email',
  },
  headerTitle: {
    id: 'pages.member.EmailUpdatePage.header.title',
    defaultMessage: 'Change Email Address',
  },
  textContent: {
    id: 'pages.member.EmailUpdatePage.text.content',
    defaultMessage:
      'Please enter your current email and password, and the new email address. We are going to ' +
      'send you a verification email to the new address.',
  },
  inputCurrentEmailTitle: {
    id: 'pages.member.EmailUpdatePage.input.currentEmailTitle',
    defaultMessage: 'Current Email',
  },
  inputNewEmailTitle: {
    id: 'pages.member.EmailUpdatePage.input.newEmailTitle',
    defaultMessage: 'New Email',
  },
  inputNewEmailPlaceholder: {
    id: 'pages.member.EmailUpdatePage.input.newEmailPlaceholder',
    defaultMessage: 'Enter your new email',
  },
  buttonCancel: {
    id: 'pages.member.EmailUpdatePage.button.cancel',
    defaultMessage: 'Cancel',
  },
  buttonChangeEmail: {
    id: 'pages.member.EmailUpdatePage.button.changeEmail',
    defaultMessage: 'Change Email',
  },
  dialogConfirmationTitle: {
    id: 'pages.member.EmailUpdatePage.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogConfirmationContent: {
    id: 'pages.member.EmailUpdatePage.dialog.confirmationContent',
    defaultMessage:
      'Are you really going to change your email? After the change request is submitted, ' +
      'your account is invalidated and redirected to the login page. Please sign in with ' +
      'your new email address.',
  },
});

function EmailUpdatePage(): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const {account} = useAuth();
  const navigate = useNavigate();
  const {register, formState, handleSubmit: validate} = useForm();
  const {errors} = formState;
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const [email, setEmail] = React.useState<string>();
  const [newEmail, setNewEmail] = React.useState<string>();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const {mutate: updateAccount, isLoading} = useMutation<unknown, unknown, AccountUpdateRequest>(
    (props: AccountUpdateRequest) => API.Accounts.update(csrfToken, props),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Accounts.CacheKey);
        setTimeout(() => navigate('/'), 1000);
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const handleGoBack = React.useCallback(() => navigate('/settings'), [navigate]);
  const handleSubmit = React.useCallback(() => {
    if (!account) {
      showError(intl.formatMessage(msgs.errorNoUser));
      return;
    } else if (!email || !newEmail) {
      showError(intl.formatMessage(msgs.errorValueNotFound));
      return;
    } else if (email !== newEmail) {
      showError(intl.formatMessage(msgs.errorInvalidEmail));
      return;
    }
    setConfirmationDialogOpen(true);
  }, [email, newEmail, showError, intl, account]);
  const handleConfirmationCancel = React.useCallback(() => setConfirmationDialogOpen(false), []);
  const handleConfirmationOk = React.useCallback(async () => {
    setConfirmationDialogOpen(false);
    if (!account) {
      showError(intl.formatMessage(msgs.errorNoUser));
      return;
    } else if (!email || !newEmail) {
      showError(intl.formatMessage(msgs.errorValueNotFound));
      return;
    } else if (email !== newEmail) {
      showError(intl.formatMessage(msgs.errorInvalidEmail));
      return;
    }
    updateAccount({email, newEmail});
  }, [email, newEmail, showError, updateAccount, intl, account]);

  return (
    <Container sx={{display: 'flex', justifyContent: 'center'}}>
      <Head title={intl.formatMessage(msgs.headerTitle)} />
      <form onSubmit={validate(handleSubmit)}>
        <Paper sx={{width: 400, mt: 8, p: 3}}>
          <Stack gap={3}>
            <Typography align="center" variant="h6">
              {intl.formatMessage(msgs.headerTitle)}
            </Typography>
            {intl.formatMessage(msgs.textContent)}
            <ValidatableField
              id="email"
              label={intl.formatMessage(msgs.inputCurrentEmailTitle)}
              fullWidth
              disabled={isLoading}
              {...{errors, register}}
              registerOptions={{
                required: true,
                maxLength: 128,
                onChange: (e) => setEmail(e.target.value),
              }}
            />
            <ValidatableField
              id="newEmail"
              label={intl.formatMessage(msgs.inputNewEmailTitle)}
              placeholder={intl.formatMessage(msgs.inputNewEmailPlaceholder)}
              fullWidth
              disabled={isLoading}
              {...{errors, register}}
              registerOptions={{
                required: true,
                maxLength: 128,
                onChange: (e) => setNewEmail(e.target.value),
              }}
            />
            <Stack direction="row" gap={3}>
              <Button disabled={isLoading} fullWidth onClick={handleGoBack}>
                {intl.formatMessage(msgs.buttonCancel)}
              </Button>
              <Button fullWidth disabled={isLoading} type="submit">
                {intl.formatMessage(msgs.buttonChangeEmail)}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </form>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title={intl.formatMessage(msgs.dialogConfirmationTitle)}
        description={intl.formatMessage(msgs.dialogConfirmationContent)}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationOk}
      />
    </Container>
  );
}

export default React.memo(EmailUpdatePage);
